import { FC, useState, useRef, useEffect, useCallback, useMemo, memo } from 'react';
import { Clock, ChevronDown } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@new/common/ui/Popover/Popover';
import { TimePickerButton } from './TimePickerButton';
import { HOURS_PER_DAY } from '@new/constants';
import {
  MINUTES_COUNT,
  MINUTE_STEP,
  DEFAULT_HOUR,
  DEFAULT_MINUTE,
  DEFAULT_TIME,
} from './constants';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const HOURS_LIST = Array.from({ length: HOURS_PER_DAY }, (_, i) => i);
const MINUTES_LIST = Array.from({ length: MINUTES_COUNT }, (_, i) => i * MINUTE_STEP);

const TimePickerComponent: FC<TimePickerProps> = ({ value, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(DEFAULT_HOUR);
  const [minutes, setMinutes] = useState(DEFAULT_MINUTE);
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h || DEFAULT_HOUR);
      setMinutes(m || DEFAULT_MINUTE);
    }
  }, [value]);

  const handleHourClick = useCallback(
    (hour: number) => {
      const h = hour.toString().padStart(2, '0');
      setHours(h);
      onChange(`${h}:${minutes}`);
    },
    [minutes, onChange]
  );

  const handleMinuteClick = useCallback(
    (minute: number) => {
      const m = minute.toString().padStart(2, '0');
      setMinutes(m);
      onChange(`${hours}:${m}`);
    },
    [hours, onChange]
  );

  const handleDone = useCallback(() => {
    setOpen(false);
  }, []);

  const triggerButton = useMemo(
    () => (
      <Button
        variant="ghost"
        className="w-full border! border-(--neutral-black-NB300)! justify-between overflow-hidden"
      >
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          {value || DEFAULT_TIME}
        </div>
        <ChevronDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    [className, value]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={triggerButton} />
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col p-3 gap-3">
          <div className="flex gap-3">
            <div className="w-20">
              <div className="text-xs font-medium text-neutral-600 mb-2 text-center">Hours</div>
              <div
                ref={hoursRef}
                className="h-48 overflow-y-auto border border-neutral-200 rounded-md"
              >
                {HOURS_LIST.map((hour) => (
                  <TimePickerButton
                    key={hour}
                    value={hour}
                    selectedValue={hours}
                    onClick={handleHourClick}
                  />
                ))}
              </div>
            </div>
            <div className="w-20">
              <div className="text-xs font-medium text-neutral-600 mb-2 text-center">Minutes</div>
              <div
                ref={minutesRef}
                className="h-48 overflow-y-auto border border-neutral-200 rounded-md"
              >
                {MINUTES_LIST.map((minute) => (
                  <TimePickerButton
                    key={minute}
                    value={minute}
                    selectedValue={minutes}
                    onClick={handleMinuteClick}
                  />
                ))}
              </div>
            </div>
          </div>
          <Button onClick={handleDone} variant="primary" className="w-full h-8!">
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const TimePicker = memo(TimePickerComponent);
